import React, { useState, useEffect } from "react";
import { Drawer, Button, Space, Tag, Popconfirm, Typography, Empty, Card, Row, Col, Switch, Dropdown, Tabs, List } from "antd";
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    StarOutlined,
    StarFilled,
    PhoneOutlined,
    MailOutlined,
    UserOutlined,
    EnvironmentOutlined,
    MoreOutlined
} from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "../../utils/hooks/redux";
import { EstablishmentLocation, Place, Contact, ActivationStatus } from "../../types";
import { fetchPlaces, addPlace, updatePlace, deletePlace } from "../../redux/features/place/placeAsyncThunk";
import { fetchContacts, addContact, updateContact, deleteContact, setPrimaryContact, updateContactOrder } from "../../redux/features/contact/contactAsyncThunk";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import { HolderOutlined } from "@ant-design/icons";
import { selectPlace } from "../../redux/features/place/placeSlice";
import { selectContact } from "../../redux/features/contact/contactSlice";
import PlaceModal from "./PlaceModal";
import ContactModal from "./ContactModal";
import CopyToClipboard from "../../components/CopyToClipboard";

const { Title, Text } = Typography;

interface PlacesContactsDrawerProps {
    visible: boolean;
    onClose: () => void;
    establishment: EstablishmentLocation | null;
}

const PlacesContactsDrawer: React.FC<PlacesContactsDrawerProps> = ({
    visible,
    onClose,
    establishment,
}) => {
    const dispatch = useAppDispatch();
    const { data: places, loading: placesLoading } = useAppSelector(selectPlace);
    const { data: contacts, loading: contactsLoading } = useAppSelector(selectContact);

    const [placeModalVisible, setPlaceModalVisible] = useState(false);
    const [contactModalVisible, setContactModalVisible] = useState(false);
    const [editingPlace, setEditingPlace] = useState<Place | null>(null);
    const [editingContact, setEditingContact] = useState<Contact | null>(null);
    const [selectedPlace, setSelectedPlace] = useState<Place | null>(null);
    const [activeTab, setActiveTab] = useState<string>("all");

    useEffect(() => {
        if (visible && establishment) {
            // Load establishment-level contacts
            dispatch(fetchContacts({ establishmentLocationId: establishment.id, status: 1 }));
            // Load places
            dispatch(fetchPlaces({ establishmentLocationId: establishment.id, status: 1 }));
        }
    }, [visible, establishment, dispatch]);

    const handleAddPlace = () => {
        setEditingPlace(null);
        setPlaceModalVisible(true);
    };

    const handleEditPlace = (place: Place) => {
        setEditingPlace(place);
        setPlaceModalVisible(true);
    };

    const handleDeletePlace = async (placeId: string) => {
        await dispatch(deletePlace({ id: placeId }));
        if (selectedPlace?.id === placeId) {
            setSelectedPlace(null);
        }
    };

    const handleTogglePlaceStatus = async (place: Place) => {
        const newStatus = place.status === ActivationStatus.Active
            ? ActivationStatus.Inactive
            : ActivationStatus.Active;

        const formData = new FormData();
        formData.append('status', String(newStatus));
        formData.append('name', place.name);
        formData.append('establishmentLocationId', place.establishmentLocationId);

        await dispatch(updatePlace({ id: place.id, formData }));
    };

    const handleSavePlace = async (values: any) => {
        const formData = new FormData();
        Object.keys(values).forEach((key) => {
            if (key === 'icon' && values[key]?.length > 0) {
                if (values[key][0].originFileObj) {
                    formData.append('icon', values[key][0].originFileObj);
                }
            } else if (values[key] !== undefined && values[key] !== null) {
                formData.append(key, values[key]);
            }
        });

        if (editingPlace) {
            await dispatch(updatePlace({ id: editingPlace.id, formData }));
        } else {
            await dispatch(addPlace({ formData }));
        }
        setPlaceModalVisible(false);
    };

    const handleAddContact = (forPlace?: Place) => {
        // Check if we've reached the limit of 10 contacts
        const currentContacts = forPlace
            ? getPlaceContacts(forPlace.id)
            : establishmentContacts;

        if (currentContacts.length >= 10) {
            alert(`Maximum of 10 contacts allowed per ${forPlace ? 'place' : 'establishment'}`);
            return;
        }

        setEditingContact(null);
        setSelectedPlace(forPlace || null);
        setContactModalVisible(true);
    };

    const handleEditContact = (contact: Contact) => {
        setEditingContact(contact);
        setContactModalVisible(true);
    };

    const handleDeleteContact = async (contactId: string) => {
        await dispatch(deleteContact({ id: contactId }));
    };

    const handleSetPrimaryContact = async (contact: Contact) => {
        await dispatch(setPrimaryContact({
            id: contact.id,
            establishmentLocationId: contact.establishmentLocationId,
            placeId: contact.placeId
        }));
    };

    const handleSaveContact = async (values: any) => {
        if (editingContact) {
            await dispatch(updateContact({ id: editingContact.id, data: values }));
        } else {
            await dispatch(addContact(values));
        }
        setContactModalVisible(false);
        setSelectedPlace(null);
    };

    const establishmentContacts = contacts
        .filter(c => c.establishmentLocationId === establishment?.id && !c.placeId)
        .slice()
        .sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
    const getPlaceContacts = (placeId: string) =>
        contacts
            .filter(c => c.placeId === placeId)
            .slice()
            .sort((a, b) => (a.index ?? 0) - (b.index ?? 0));

    const renderContactCard = (contact: Contact, showPlaceName: boolean = false) => (
        <Card
            size="small"
            style={{ marginBottom: 8 }}
        >
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <UserOutlined />
                        <Text strong>{contact.name}</Text>
                        {contact.isPrimary && <Tag color="gold">Primary</Tag>}
                    </div>
                    <Space size="small">
                        <Button
                            type="text"
                            size="small"
                            icon={contact.isPrimary ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />}
                            onClick={() => handleSetPrimaryContact(contact)}
                            style={{ color: '#8c8c8c' }}
                        />
                        <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleEditContact(contact)}
                            style={{ color: '#8c8c8c' }}
                        />
                        <Dropdown
                            menu={{
                                items: [
                                    {
                                        key: 'delete',
                                        danger: true,
                                        label: 'Delete',
                                        icon: <DeleteOutlined />,
                                        onClick: () => {
                                            // Show popconfirm
                                            if (window.confirm('Are you sure you want to delete this contact?')) {
                                                handleDeleteContact(contact.id);
                                            }
                                        }
                                    }
                                ]
                            }}
                            trigger={['click']}
                        >
                            <Button
                                type="text"
                                size="small"
                                icon={<MoreOutlined />}
                                style={{ color: '#8c8c8c' }}
                            />
                        </Dropdown>
                    </Space>
                </div>
                {contact.title && <CopyToClipboard text={contact.title} size="small"><Text type="secondary">{contact.title}</Text></CopyToClipboard>}
                {contact.department && <CopyToClipboard text={contact.department} size="small"><Text type="secondary"> - {contact.department}</Text></CopyToClipboard>}
                {contact.email && (
                    <div>
                        <MailOutlined style={{ marginRight: 8 }} />
                        <CopyToClipboard text={contact.email} size="small"><Text>{contact.email}</Text></CopyToClipboard>
                    </div>
                )}
                {contact.phone && (
                    <div>
                        <PhoneOutlined style={{ marginRight: 8 }} />
                        <CopyToClipboard text={contact.phone} size="small"><Text>{contact.phone}</Text></CopyToClipboard>
                    </div>
                )}
                {contact.mobilePhone && (
                    <div>
                        <PhoneOutlined style={{ marginRight: 8 }} />
                        <CopyToClipboard text={contact.mobilePhone} size="small"><Text>{contact.mobilePhone}</Text></CopyToClipboard> <Tag>Mobile</Tag>
                    </div>
                )}
                {contact.address && (
                    <div>
                        <EnvironmentOutlined style={{ marginRight: 8 }} />
                        <CopyToClipboard text={contact.address} size="small"><Text type="secondary">{contact.address}</Text></CopyToClipboard>
                    </div>
                )}
                {contact.website && (
                    <div>
                        <Text type="secondary">Website: </Text>
                        <CopyToClipboard text={contact.website} size="small">
                            <a href={contact.website} target="_blank" rel="noopener noreferrer">
                                {contact.website}
                            </a>
                        </CopyToClipboard>
                    </div>
                )}
                {contact.lostAndFoundFormLink && (
                    <div>
                        <Text type="secondary">Lost & Found Form: </Text>
                        <CopyToClipboard text={contact.lostAndFoundFormLink} size="small">
                            <a href={contact.lostAndFoundFormLink} target="_blank" rel="noopener noreferrer">
                                {contact.lostAndFoundFormLink}
                            </a>
                        </CopyToClipboard>
                    </div>
                )}
                {contact.notes && (
                    <div style={{ marginTop: 8, padding: 8, background: '#f5f5f5', borderRadius: 4 }}>
                        <CopyToClipboard text={contact.notes} size="small"><Text type="secondary" style={{ fontSize: 12 }}>{contact.notes}</Text></CopyToClipboard>
                    </div>
                )}
            </Space>
        </Card>
    );

    return (
        <>
            <Drawer
                title={`Manage: ${establishment?.name || ''}`}
                placement="right"
                onClose={onClose}
                open={visible}
                width={800}
            >
                {/* Action Buttons */}
                <Space style={{ width: "100%", marginBottom: 16 }} size="middle">
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => handleAddContact()}
                        disabled={establishmentContacts.length >= 10}
                    >
                        Add Establishment Contact {establishmentContacts.length >= 10 && '(Max 10)'}
                    </Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAddPlace}>
                        Add Place
                    </Button>
                </Space>

                <Tabs activeKey={activeTab} onChange={setActiveTab}
                    items={[
                        {
                            key: "all",
                            label: `All Contacts (${establishmentContacts.length + places.reduce((s, p) => s + getPlaceContacts(p.id).length, 0)})`,
                            children: (() => {
                                // Mixed list: establishment contacts + place contacts (grouped as individual entries)
                                type MixedItem = { type: "contact"; id: string; index: number; data: Contact; placeName?: string; placeId?: string };
                                const placesWithContacts = places.filter(p => getPlaceContacts(p.id).length > 0);
                                const placeContactItems: MixedItem[] = placesWithContacts.flatMap(p =>
                                    getPlaceContacts(p.id)
                                        .slice()
                                        .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
                                        .map(c => ({ type: "contact" as const, id: `pc-${c.id}`, index: c.index ?? 0, data: c, placeName: p.name, placeId: p.id }))
                                );
                                const establishmentItems: MixedItem[] = establishmentContacts.map(c => ({
                                    type: "contact" as const, id: `ec-${c.id}`, index: c.index ?? 0, data: c,
                                }));
                                const mixedItems = [...establishmentItems, ...placeContactItems].sort((a, b) => a.index - b.index);

                                if (mixedItems.length === 0) {
                                    return <Empty description="No contacts yet. Add a contact or place contact using the buttons above." image={Empty.PRESENTED_IMAGE_SIMPLE} />;
                                }

                                return (
                                    <DragDropContext
                                        onDragEnd={(result: DropResult) => {
                                            if (!result.destination) return;
                                            const reordered = Array.from(mixedItems);
                                            const [moved] = reordered.splice(result.source.index, 1);
                                            reordered.splice(result.destination.index, 0, moved);
                                            const orderUpdates = reordered.map((item, i) => ({ id: item.data.id, index: i }));
                                            dispatch(updateContactOrder({ orderUpdates }));
                                        }}
                                    >
                                        <Droppable droppableId="all-contacts-list">
                                            {(provided) => (
                                                <div ref={provided.innerRef} {...provided.droppableProps}>
                                                    {mixedItems.map((item, index) => (
                                                        <Draggable key={item.id} draggableId={item.id} index={index}>
                                                            {(dragProvided) => (
                                                                <div
                                                                    ref={dragProvided.innerRef}
                                                                    {...dragProvided.draggableProps}
                                                                    style={{ ...dragProvided.draggableProps.style, display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 8 }}
                                                                >
                                                                    <div {...dragProvided.dragHandleProps} style={{ cursor: "grab", padding: "16px 4px 0 0", color: "#9ca3af" }}>
                                                                        <HolderOutlined />
                                                                    </div>
                                                                    <div style={{ flex: 1 }}>
                                                                        <div style={{ position: "relative" }}>
                                                                            <Tag color={item.placeName ? "green" : "blue"} style={{ position: "absolute", top: -6, left: 8, zIndex: 1, fontSize: 10 }}>
                                                                                {item.placeName ? `📍 ${item.placeName}` : "📇 Establishment"}
                                                                            </Tag>
                                                                            {renderContactCard(item.data)}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </Draggable>
                                                    ))}
                                                    {provided.placeholder}
                                                </div>
                                            )}
                                        </Droppable>
                                    </DragDropContext>
                                );
                            })(),
                        },
                        {
                            key: "contacts",
                            label: `📇 Establishment Contacts (${establishmentContacts.length})`,
                            children: establishmentContacts.length === 0 ? (
                                <Empty description="No establishment contacts yet." image={Empty.PRESENTED_IMAGE_SIMPLE} />
                            ) : (
                                <DragDropContext
                                    onDragEnd={(result: DropResult) => {
                                        if (!result.destination) return;
                                        const reordered = Array.from(establishmentContacts);
                                        const [moved] = reordered.splice(result.source.index, 1);
                                        reordered.splice(result.destination.index, 0, moved);
                                        const orderUpdates = reordered.map((c, i) => ({ id: c.id, index: i }));
                                        dispatch(updateContactOrder({ orderUpdates }));
                                    }}
                                >
                                    <Droppable droppableId="establishment-contacts-tab">
                                        {(provided) => (
                                            <div ref={provided.innerRef} {...provided.droppableProps}>
                                                {establishmentContacts.map((contact, index) => (
                                                    <Draggable key={contact.id} draggableId={contact.id} index={index}>
                                                        {(dragProvided) => (
                                                            <div
                                                                ref={dragProvided.innerRef}
                                                                {...dragProvided.draggableProps}
                                                                style={{ ...dragProvided.draggableProps.style, display: "flex", alignItems: "flex-start", gap: 8 }}
                                                            >
                                                                <div {...dragProvided.dragHandleProps} style={{ cursor: "grab", padding: "16px 4px 0 0", color: "#9ca3af" }}>
                                                                    <HolderOutlined />
                                                                </div>
                                                                <div style={{ flex: 1 }}>{renderContactCard(contact)}</div>
                                                            </div>
                                                        )}
                                                    </Draggable>
                                                ))}
                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>
                                </DragDropContext>
                            ),
                        },
                        {
                            key: "places",
                            label: `📍 Places (${places.length})`,
                            children: places.length === 0 ? (
                                <Empty description="No places yet. Add places like Terminal 1, Gate A, etc." image={Empty.PRESENTED_IMAGE_SIMPLE} />
                            ) : (
                                <List
                                    dataSource={places}
                                    renderItem={(place) => {
                                        const placeContacts = getPlaceContacts(place.id);
                                        return (
                                            <Card
                                                title={
                                                    <Space>
                                                        {place.icon && <img src={place.fullUrl} alt={place.name} style={{ width: 24, height: 24 }} />}
                                                        <Text strong>{place.name}</Text>
                                                        {place.description && <Text type="secondary">- {place.description}</Text>}
                                                    </Space>
                                                }
                                                style={{ marginBottom: 16 }}
                                                extra={
                                                    <Space>
                                                        <Switch
                                                            checked={place.status === ActivationStatus.Active}
                                                            onChange={() => handleTogglePlaceStatus(place)}
                                                            checkedChildren="Enabled"
                                                            unCheckedChildren="Disabled"
                                                        />
                                                        <Button size="small" icon={<EditOutlined />} onClick={() => handleEditPlace(place)}>Edit</Button>
                                                        <Popconfirm
                                                            title="Are you sure? This will delete all contacts in this place."
                                                            onConfirm={() => handleDeletePlace(place.id)}
                                                            okText="Yes" cancelText="No"
                                                        >
                                                            <Button size="small" icon={<DeleteOutlined />} danger>Delete</Button>
                                                        </Popconfirm>
                                                    </Space>
                                                }
                                            >
                                                <Space direction="vertical" style={{ width: '100%' }}>
                                                    {place.address && (
                                                        <div>
                                                            <EnvironmentOutlined style={{ marginRight: 8 }} />
                                                            <Text>{place.address}</Text>
                                                            {place.city && <Text>, {place.city}</Text>}
                                                            {place.state && <Text>, {place.state}</Text>}
                                                        </div>
                                                    )}
                                                    <div style={{ marginTop: 16 }}>
                                                        <Row align="middle" style={{ marginBottom: 8 }}>
                                                            <Col flex="auto">
                                                                <Title level={5}>📇 Place Contacts ({placeContacts.length})</Title>
                                                            </Col>
                                                            <Col>
                                                                <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={() => handleAddContact(place)} disabled={placeContacts.length >= 10}>
                                                                    Add Contact {placeContacts.length >= 10 && '(Max 10)'}
                                                                </Button>
                                                            </Col>
                                                        </Row>
                                                        {placeContacts.length === 0 ? (
                                                            <Empty description="No contacts for this place yet." image={Empty.PRESENTED_IMAGE_SIMPLE} />
                                                        ) : (
                                                            <DragDropContext
                                                                onDragEnd={(result: DropResult) => {
                                                                    if (!result.destination) return;
                                                                    const reordered = Array.from(placeContacts);
                                                                    const [moved] = reordered.splice(result.source.index, 1);
                                                                    reordered.splice(result.destination.index, 0, moved);
                                                                    const orderUpdates = reordered.map((c, i) => ({ id: c.id, index: i }));
                                                                    dispatch(updateContactOrder({ orderUpdates }));
                                                                }}
                                                            >
                                                                <Droppable droppableId={`place-${place.id}-contacts`}>
                                                                    {(provided) => (
                                                                        <div ref={provided.innerRef} {...provided.droppableProps}>
                                                                            {placeContacts.map((contact, index) => (
                                                                                <Draggable key={contact.id} draggableId={contact.id} index={index}>
                                                                                    {(dragProvided) => (
                                                                                        <div
                                                                                            ref={dragProvided.innerRef}
                                                                                            {...dragProvided.draggableProps}
                                                                                            style={{ ...dragProvided.draggableProps.style, display: "flex", alignItems: "flex-start", gap: 8 }}
                                                                                        >
                                                                                            <div {...dragProvided.dragHandleProps} style={{ cursor: "grab", padding: "16px 4px 0 0", color: "#9ca3af" }}>
                                                                                                <HolderOutlined />
                                                                                            </div>
                                                                                            <div style={{ flex: 1 }}>{renderContactCard(contact, true)}</div>
                                                                                        </div>
                                                                                    )}
                                                                                </Draggable>
                                                                            ))}
                                                                            {provided.placeholder}
                                                                        </div>
                                                                    )}
                                                                </Droppable>
                                                            </DragDropContext>
                                                        )}
                                                    </div>
                                                </Space>
                                            </Card>
                                        );
                                    }}
                                />
                            ),
                        },
                    ]}
                />
            </Drawer>

            <PlaceModal
                visible={placeModalVisible}
                onCancel={() => {
                    setPlaceModalVisible(false);
                    setEditingPlace(null);
                }}
                onSave={handleSavePlace}
                editingRecord={editingPlace}
                establishmentLocationId={establishment?.id || ''}
                loading={placesLoading}
            />

            <ContactModal
                visible={contactModalVisible}
                onCancel={() => {
                    setContactModalVisible(false);
                    setEditingContact(null);
                    setSelectedPlace(null);
                }}
                onSave={handleSaveContact}
                editingRecord={editingContact}
                establishmentLocationId={establishment?.id}
                placeId={selectedPlace?.id}
                loading={contactsLoading}
            />
        </>
    );
};

export default PlacesContactsDrawer;
